import { Component, useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { DealNote } from "@edge/shared";
import {
  ApiError,
  fetchDealNotes,
  fetchIntakeDealCurrent,
  enrichIntakeDeal,
} from "../../api";
import type { EnrichPayload } from "../../types/intake";
import { TabPanel } from "./TabPanel";
import { IntakeCapture } from "../intake/IntakeCapture";
import { Markdown } from "../../lib/markdown";

class IntakeErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: Error) {
    console.error("[IntakeNoteTab] render error:", err);
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "America/Los_Angeles",
});

/** Intake-related notes only, newest first. */
function isIntakeNote(n: DealNote): boolean {
  const t = (n.title ?? "").trim();
  return t.startsWith("Intake Summary -") || t.startsWith("Call Notes -");
}

interface Props {
  dealId: string;
}

export function IntakeNoteTab({ dealId }: Props) {
  const [notes, setNotes] = useState<DealNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Enrich capture flow.
  const [capturing, setCapturing] = useState(false);
  const [current, setCurrent] = useState<Record<string, unknown> | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(false);

  const loadNotes = useCallback(async () => {
    try {
      const all = await fetchDealNotes(dealId);
      setNotes(all.filter(isIntakeNote).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "fetch_failed",
      );
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    setLoading(true);
    setCapturing(false);
    setCurrent(null);
    void loadNotes();
  }, [dealId, loadNotes]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.includes("⚠") ? 6000 : 3000);
    return () => clearTimeout(t);
  }, [toast]);

  async function startCapture() {
    setLoadingCurrent(true);
    try {
      const { current: cur } = await fetchIntakeDealCurrent(dealId);
      setCurrent(cur);
      setCapturing(true);
    } catch (err) {
      setToast(err instanceof ApiError ? `⚠ ${err.code}` : "⚠ couldn't load deal values");
    } finally {
      setLoadingCurrent(false);
    }
  }

  async function handleEnrich(payload: EnrichPayload) {
    const res = await enrichIntakeDeal(payload);
    const bits: string[] = [];
    if (res.updated_fields.length) bits.push(`${res.updated_fields.length} field(s)`);
    if (res.loan_request_notes_written) bits.push("lender paragraph");
    if (res.call_note_saved) bits.push("call note");
    setToast(
      res.call_note_saved === false
        ? "✓ Saved — ⚠ call note didn't save, retry from chat/manual"
        : `✓ Saved${bits.length ? " — " + bits.join(" · ") : " (no changes)"}`,
    );
    setCapturing(false);
    setCurrent(null);
    await loadNotes();
  }

  if (capturing && current) {
    return (
      <TabPanel>
        <IntakeCapture
          variant="enrich"
          current={current}
          dealId={dealId}
          onEnrich={handleEnrich}
          onCancel={() => {
            setCapturing(false);
            setCurrent(null);
          }}
        />
        {toast && <div className="edge-toast edge-toast-dismissable" onClick={() => setToast(null)}>{toast}</div>}
      </TabPanel>
    );
  }

  return (
    <IntakeErrorBoundary
      fallback={
        <TabPanel>
          <div className="lme-error">⚠ INTAKE render error — refresh to retry.</div>
        </TabPanel>
      }
    >
      <TabPanel>
        <div className="intake-note-tab">
          <div className="intake-note-tab-head">
            <span className="intake-note-tab-title">INTAKE HISTORY</span>
            <button
              className="intake-note-add"
              onClick={() => void startCapture()}
              disabled={loadingCurrent}
            >
              {loadingCurrent ? "…" : "✦ ADD TO THIS DEAL"}
            </button>
          </div>

          {loading ? (
            <div className="lme-loading">
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-label">LOADING INTAKE</span>
            </div>
          ) : error ? (
            <div className="lme-error">
              ⚠ {error}
              <button className="lme-retry" onClick={() => { setLoading(true); void loadNotes(); }}>
                RETRY
              </button>
            </div>
          ) : notes.length === 0 ? (
            <div className="intake-note-empty">
              No intake notes yet. Use “Add to this deal” to capture intake (chat, voice, photo, or
              manual) — it writes a dated note and merges any field updates.
            </div>
          ) : (
            <div className="intake-note-list">
              {notes.map((n) => (
                <div className="intake-note-card" key={n.id}>
                  <div className="intake-note-card-head">
                    <span className="intake-note-card-title">{n.title ?? "(untitled)"}</span>
                    <span className="intake-note-card-date">{DATE_FMT.format(new Date(n.createdAt))}</span>
                  </div>
                  <Markdown text={n.content} />
                  {n.createdBy && <div className="intake-note-card-author">— {n.createdBy}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {toast && <div className="edge-toast edge-toast-dismissable" onClick={() => setToast(null)}>{toast}</div>}
      </TabPanel>
    </IntakeErrorBoundary>
  );
}
